// run.ts is the one call the generated client cannot make.
//
// A run answers with an event stream rather than a body, so it is not in the
// OpenAPI document and there is no generated function for it. This is that
// function, written once, with the same credential and organisation policy
// every other request carries — read from the same sources the transport uses,
// so the two cannot disagree about who is calling.
//
// It uses fetch and a reader rather than EventSource, for two reasons that
// both matter here: EventSource cannot POST, and it cannot set headers, so it
// could carry neither the message nor the credential.

import { AUTH_MODE, BASE, ApiError } from './client';

/** One thing that happened during a run, as it arrives. */
export interface RunEvent {
  type:
    | 'user'
    | 'execute_js'
    | 'execute_js_result'
    | 'sandbox_event'
    | 'data_update'
    | 'compacted'
    | 'budget_trimmed'
    | 'response'
    | 'response_chunk'
    | 'input_request'
    | 'warning'
    | 'error'
    | 'done';
  content?: string;
  tool?: string;
  args?: Record<string, unknown>;
  tokens?: { prompt: number; completion: number };
  summary?: {
    sessionId: string;
    steps: number;
    tokens: { prompt: number; completion: number };
    dataBytesCarried: number;
  };
}

export interface RunOptions {
  sessionId: string;
  message: string;
  credential: string | null;
  orgId: string | null;
  onEvent: (event: RunEvent) => void;
  signal?: AbortSignal;
}

/**
 * Starts a run and calls onEvent for each turn as it arrives.
 *
 * Resolves when the stream closes. A run that fails sends an `error` event and
 * then closes — the stream is already open by the time most failures happen, so
 * there is no status left to fail with, and a caller that only watched for
 * rejections would see a run simply stop.
 */
export async function runAgent(opts: RunOptions): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (opts.orgId) headers['X-Org-ID'] = opts.orgId;
  if (opts.credential) {
    headers[AUTH_MODE === 'header' ? 'X-Test-Subject' : 'Authorization'] =
      AUTH_MODE === 'header' ? opts.credential : `Bearer ${opts.credential}`;
  }

  const response = await fetch(`${BASE}/agent-sessions/${encodeURIComponent(opts.sessionId)}/run`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message: opts.message }),
    signal: opts.signal,
  });
  if (!response.ok || !response.body) {
    let problem: unknown = { message: response.statusText };
    try {
      problem = await response.json();
    } catch {
      // A refusal before the stream opened may not be JSON; the status is
      // still the useful part.
    }
    throw new ApiError(response.status, problem as never);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Events are separated by a blank line. Anything after the last one is a
    // partial event and stays in the buffer for the next read — splitting on
    // newlines alone would deliver half a JSON payload.
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data: '));
      if (line) {
        try {
          opts.onEvent(JSON.parse(line.slice(6)) as RunEvent);
        } catch {
          // A malformed event is dropped rather than ending the run: the
          // turns after it are still worth showing.
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

/** Answers a question the agent asked with ask(). */
export async function answerAgent(
  sessionId: string,
  requestId: string,
  value: unknown,
  credential: string | null,
  orgId: string | null,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (orgId) headers['X-Org-ID'] = orgId;
  if (credential) {
    headers[AUTH_MODE === 'header' ? 'X-Test-Subject' : 'Authorization'] =
      AUTH_MODE === 'header' ? credential : `Bearer ${credential}`;
  }
  const response = await fetch(`${BASE}/agent-sessions/${encodeURIComponent(sessionId)}/input`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ requestId, value }),
  });
  if (!response.ok) throw new ApiError(response.status, await response.json());
}
